import { AlertTriangle, Loader2, ShieldCheck, UserCheck, UserPlus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { inviteAuthPath } from '../../lib/invite-links';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { AgenticFlowByline } from '../agentic-flow-byline';
import { BrandLockup } from '../brand-lockup';

type AcceptInvitePageProps = {
  brandName: string;
  /** The product's own mark, e.g. `<InboxClinicLogo />` — this is often a recipient's first
   *  screen, so it leads with the brand rather than a generic icon. */
  logo: ReactNode;
  currentUserEmail?: string | null;
  onSwitchAccount: () => void;
};

/** Shared capability-link surface; each portal supplies only its product identity and sign-out. */
export function AcceptInvitePage({
  brandName,
  logo,
  currentUserEmail,
  onSwitchAccount,
}: AcceptInvitePageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const {
    data: invite,
    isLoading,
    error,
  } = trpc.workspace.getInvitationDetails.useQuery(
    { token },
    { enabled: Boolean(token), retry: false }
  );

  const acceptMutation = trpc.workspace.acceptInvitation.useMutation({
    onSuccess: (data) => {
      localStorage.setItem('workspaceId', data.workspaceId.toString());
      if (data.organizationId)
        localStorage.setItem('organizationId', data.organizationId.toString());
      toast.success('Successfully joined the workspace!');
      if (data.seatAssignmentIssues.length) {
        toast.warning(
          'You joined, but an admin still needs to assign some requested product access.'
        );
      }
      navigate(`/org/${data.organizationId}/workspace/${data.workspaceId}/dashboard`);
    },
    onError: (mutationError) => {
      setAcceptError(mutationError.message || 'Failed to accept invitation.');
      toast.error(mutationError.message || 'Failed to accept invitation.');
      setIsAccepting(false);
    },
  });

  const isEmailMismatch = acceptError?.startsWith('This invite was sent to');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f19] p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Fetching invitation details...</p>
        </div>
      </div>
    );
  }

  if (!token || error || !invite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f19] p-4">
        <Card className="w-full max-w-md border-destructive/20 bg-card/50 p-2 shadow-xl backdrop-blur-sm">
          <CardHeader className="pb-4 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">Invalid invitation</CardTitle>
            <CardDescription className="mt-1">
              {error?.message ||
                'This invitation code is invalid, expired, or has already been accepted.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Button onClick={() => navigate('/login')} className="w-full rounded-xl">
              Back to login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0f19] p-4">
      <div className="w-full max-w-md space-y-6">
        <BrandLockup brandName={brandName} logo={logo} />
        <Card className="border-border/50 bg-card/50 p-2 shadow-2xl backdrop-blur-sm">
          <CardHeader className="pb-4 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-primary/10">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">
              You&apos;ve been invited
            </CardTitle>
            <CardDescription className="mt-1">Join a workspace to collaborate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pb-6">
            <div className="space-y-2 rounded-xl border border-border bg-background/50 p-4 text-center">
              <p className="text-sm leading-relaxed text-muted-foreground">
                <strong className="text-foreground">{invite.inviterName || 'A colleague'}</strong>{' '}
                invited you to collaborate in:
              </p>
              <h3 className="text-lg font-bold tracking-tight text-primary">
                {invite.workspaceName}
              </h3>
              <span className="mt-1 inline-block rounded border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">
                Role: {invite.role || 'Viewer'}
              </span>
              <p className="text-xs text-muted-foreground">
                Join {invite.memberCount} teammate{invite.memberCount === 1 ? '' : 's'} ·{' '}
                {invite.threadCount} shared thread{invite.threadCount === 1 ? '' : 's'} ·{' '}
                {invite.taskCount} task{invite.taskCount === 1 ? '' : 's'}
              </p>
            </div>

            {currentUserEmail ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
                  <UserCheck className="h-4 w-4 shrink-0 text-primary" />
                  <span>
                    Signed in as <strong>{currentUserEmail}</strong>.
                  </span>
                </div>
                {isEmailMismatch && (
                  <div className="space-y-2 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-xs text-destructive">
                    <p>{acceptError}</p>
                    <Button variant="outline" size="sm" onClick={onSwitchAccount}>
                      Sign in with invited account
                    </Button>
                  </div>
                )}
                <Button
                  onClick={() => {
                    setAcceptError(null);
                    setIsAccepting(true);
                    acceptMutation.mutate({ token });
                  }}
                  disabled={isAccepting}
                  className="h-11 w-full rounded-xl text-md"
                >
                  {isAccepting ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <ShieldCheck className="mr-2 h-5 w-5" />
                  )}
                  Accept & enter workspace
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-center text-xs text-muted-foreground">
                  This invitation is for <strong className="text-foreground">{invite.email}</strong>
                  . Create your account with that address to accept it.
                </p>
                <Button
                  onClick={() => navigate(inviteAuthPath('register', token))}
                  className="h-11 w-full rounded-xl text-md"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  Create account & accept
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(inviteAuthPath('login', token))}
                  className="h-11 w-full rounded-xl text-md"
                >
                  I already have an account
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <p className="text-center">
          <AgenticFlowByline />
        </p>
      </div>
    </div>
  );
}
